import React, { useState } from 'react';
import { useTeacher } from '@/context/TeacherContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import {
    Grid3X3,
    ChevronDown,
    Menu,
    Globe,
    Bell,
    User,
    LogOut,
    Settings,
    HelpCircle
} from 'lucide-react';
import { clearAuthTokens, clearAllSessions } from '@/utils/auth';

// --- Class Switcher Component (Internal) ---
const ClassSwitcher = () => {
    const { classes, currentClass, setCurrentClass, teacherTheme } = useTeacher();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isDark = teacherTheme === 'dark';

    const viewMode = currentClass ? 'class' : 'overview';

    const onSelectClass = (cls) => setCurrentClass(cls);
    const onGoToOverview = () => setCurrentClass(null);

    const getSubjectIcon = (subject) => {
        switch (subject?.toLowerCase()) {
            case 'physics': return '⚛️';
            case 'mathematics': return '📐';
            case 'chemistry': return '🧪';
            case 'biology': return '🔬';
            default: return '📚';
        }
    };

    const getClassStatusColor = (classItem) => {
        // Mock logic for status color based on average score (if available in mock data)
        // Assuming mock data might not have averageScore, defaulting to 'good'
        const score = classItem.averageScore || 80;
        if (score >= 85) return 'excellent';
        if (score >= 75) return 'good';
        if (score >= 65) return 'average';
        return 'needs-help';
    };

    return (
        <div className="relative">
            <div
                className={cn(
                    "flex items-center border rounded-lg px-4 py-2 cursor-pointer transition-colors w-72",
                    isDark
                        ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] hover:bg-[hsl(var(--sidebar-hover))]"
                        : "bg-[#D4C4A8] border-[#C9B08A] hover:bg-[#CDB89A]"
                )}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <div className="flex items-center gap-3 flex-1">
                    {viewMode === 'overview' ? (
                        <>
                            <div className={cn(
                                "p-1.5 rounded-md",
                                isDark
                                    ? "bg-[hsl(var(--gold-primary))]/20 text-[hsl(var(--gold-primary))]"
                                    : "bg-white text-[#C9A961]"
                            )}>
                                <Grid3X3 size={16} />
                            </div>
                            <div className="flex flex-col">
                                <span className={cn("text-base font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Class Selector</span>
                                <span className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{classes.length} classes</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="text-lg">
                                {getSubjectIcon(currentClass?.subject || '')}
                            </div>
                            <div className="flex flex-col">
                                <span className={cn("text-base font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                                    {currentClass?.fullName}
                                </span>
                                <span className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{currentClass?.students} students</span>
                            </div>
                        </>
                    )}
                </div>
                <ChevronDown size={16} className={cn("transition-transform flex-shrink-0", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]", isDropdownOpen ? "rotate-180" : "")} />
            </div>

            {isDropdownOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsDropdownOpen(false)}
                    ></div>
                    <div className={cn(
                        "absolute top-full left-0 mt-2 w-72 rounded-xl shadow-lg z-50",
                        isDark
                            ? "bg-[hsl(var(--card))] border border-[hsl(var(--card-border))]"
                            : "bg-[#FAF8F5] border border-[#E8E1D6]"
                    )}>
                        <div
                            className={cn(
                                "flex items-center gap-3 p-3 cursor-pointer rounded-t-xl",
                                isDark
                                    ? "hover:bg-[hsl(var(--sidebar-hover))]"
                                    : "hover:bg-[#FAF8F5]",
                                viewMode === 'overview'
                                    ? isDark
                                        ? "bg-[hsl(var(--sidebar-hover))]"
                                        : "bg-[#F5F1EB]"
                                    : ""
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                onGoToOverview();
                                setIsDropdownOpen(false);
                            }}
                        >
                            <div className={cn(
                                "p-1.5 rounded-md",
                                isDark
                                    ? "bg-[hsl(var(--gold-primary))]/20 text-[hsl(var(--gold-primary))]"
                                    : "bg-[#E8DCC8] text-[#C9A961]"
                            )}>
                                <Grid3X3 size={16} />
                            </div>
                            <div className="flex-1">
                                <div className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>All Classes Overview</div>
                                <div className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>View summary of all {classes.length} classes</div>
                            </div>
                            {viewMode === 'overview' && (
                                <div className={cn(isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>•</div>
                            )}
                        </div>

                        <div className={cn("border-t my-1", isDark ? "border-[hsl(var(--border))]" : "border-[#E8E1D6]")}></div>

                        <div className="px-3 py-2">
                            <div className={cn("text-xs font-medium uppercase tracking-wider mb-2", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Individual Classes</div>
                            <div className="space-y-1 max-h-60 overflow-y-auto">
                                {classes.map((classItem) => (
                                    <div
                                        key={classItem.id}
                                        className={cn(
                                            "flex items-center gap-3 p-3 cursor-pointer rounded-lg",
                                            isDark
                                                ? "hover:bg-[hsl(var(--sidebar-hover))]"
                                                : "hover:bg-[#FAF8F5]",
                                            currentClass?.id === classItem.id
                                                ? isDark
                                                    ? "bg-[hsl(var(--sidebar-hover))]"
                                                    : "bg-[#F5F1EB]"
                                                : ""
                                        )}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelectClass(classItem);
                                            setIsDropdownOpen(false);
                                        }}
                                    >
                                        <div className="text-lg">
                                            {getSubjectIcon(classItem.subject)}
                                        </div>
                                        <div className="flex-1">
                                            <div className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                                {classItem.fullName}
                                            </div>
                                            <div className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                                                {classItem.students} students
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className={cn(
                                                "w-2 h-2 rounded-full",
                                                isDark
                                                    ? getClassStatusColor(classItem) === 'excellent' && "bg-[hsl(var(--gold-primary))]"
                                                    : getClassStatusColor(classItem) === 'excellent' && "bg-[#C9A961]",
                                                isDark
                                                    ? getClassStatusColor(classItem) === 'good' && "bg-[hsl(var(--gold-primary))]/70"
                                                    : getClassStatusColor(classItem) === 'good' && "bg-[#D4AF77]",
                                                isDark
                                                    ? getClassStatusColor(classItem) === 'average' && "bg-[hsl(var(--muted-foreground))]"
                                                    : getClassStatusColor(classItem) === 'average' && "bg-[#E8DCC8]",
                                                isDark
                                                    ? getClassStatusColor(classItem) === 'needs-help' && "bg-[hsl(var(--muted-foreground))]/50"
                                                    : getClassStatusColor(classItem) === 'needs-help' && "bg-[#8B7B6B]"
                                            )} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// --- Main Header Component ---
export default function TeacherHeader({ onOpenSidebar }) {
    const navigate = useNavigate();
    const { teacherTheme, toggleTheme, user } = useTeacher();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const isDark = teacherTheme === 'dark';

    const handleLogoutConfirmed = () => {
        try {
            clearAuthTokens('teacher');
            clearAllSessions();
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            setShowLogoutConfirm(false);
            window.location.href = '/login';
        }
    };

    return (
        <header className={cn(
            "h-16 px-6 flex items-center justify-between border-b z-30 transition-colors",
            isDark
                ? "bg-[hsl(var(--card))] border-[hsl(var(--border))]"
                : "bg-white border-[#E8E1D6]"
        )}>
            {/* Left Side: Class Selector */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        "w-9 h-9 rounded-lg lg:hidden",
                        isDark ? "hover:bg-[hsl(var(--sidebar-hover))]" : "hover:bg-[#F5F1EB]"
                    )}
                    onClick={onOpenSidebar}
                >
                    <Menu className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                </Button>
                <ClassSwitcher />
            </div>

            {/* Right Side: Global Controls */}
            <div className="flex items-center gap-3">
                {/* Theme Toggle */}
                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />

                {/* Language Selector (Mock) */}
                <Button variant="ghost" size="icon" className={cn(
                    "w-9 h-9 rounded-lg",
                    isDark ? "hover:bg-[hsl(var(--sidebar-hover))]" : "hover:bg-[#F5F1EB]"
                )}>
                    <Globe className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                </Button>

                {/* Notifications (Mock) */}
                <div className="relative">
                    <Button variant="ghost" size="icon" className={cn(
                        "w-9 h-9 rounded-lg",
                        isDark ? "hover:bg-[hsl(var(--sidebar-hover))]" : "hover:bg-[#F5F1EB]"
                    )}>
                        <Bell className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                    </Button>
                    {user.notifications > 0 && (
                        <span className={cn(
                            "absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2",
                            isDark ? "border-[hsl(var(--card))]" : "border-white"
                        )} />
                    )}
                </div>

                {/* Profile Dropdown (Mock) */}
                <div className="relative">
                    <button
                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                        className={cn(
                            "flex items-center gap-3 ml-2 pl-2 border-l hover:opacity-80 transition-opacity",
                            isDark ? "border-[hsl(var(--border))]" : "border-[#E8E1D6]"
                        )}
                    >
                        <div className="text-right hidden md:block">
                            <div className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                {user.name}
                            </div>
                            <div className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                                {user.role}
                            </div>
                        </div>
                        <div className={cn(
                            "w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0",
                            isDark ? "bg-[hsl(var(--gold-primary))]/15 text-[hsl(var(--gold-primary))]" : "bg-[#C9A961]/10 text-[#C9A961]"
                        )}>
                            <User className="w-5 h-5" />
                        </div>
                    </button>

                    {isProfileDropdownOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsProfileDropdownOpen(false)}
                            ></div>
                            <div className={cn(
                                "absolute top-full right-0 mt-2 w-48 rounded-lg shadow-lg z-50",
                                isDark ? "bg-[hsl(var(--card))] border border-[hsl(var(--border))]" : "bg-white border border-[#E8E1D6]"
                            )}>
                                <div className="p-2 space-y-1">
                                    <button
                                        onClick={() => {
                                            navigate('/teacher/profile');
                                            setIsProfileDropdownOpen(false);
                                        }}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                            isDark ? "hover:bg-[hsl(var(--sidebar-hover))]" : "hover:bg-[#F5F1EB]",
                                            isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"
                                        )}>
                                        <User className="w-4 h-4" />
                                        <span>My Profile</span>
                                    </button>
                                    <button className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                        isDark ? "hover:bg-[hsl(var(--sidebar-hover))]" : "hover:bg-[#F5F1EB]",
                                        isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"
                                    )}>
                                        <Settings className="w-4 h-4" />
                                        <span>Settings</span>
                                    </button>
                                    <div className={cn(
                                        "my-1",
                                        isDark ? "border-t border-[hsl(var(--border))]" : "border-t border-[#E8E1D6]"
                                    )} />
                                    <button
                                        onClick={() => {
                                            setIsProfileDropdownOpen(false);
                                            setShowLogoutConfirm(true);
                                        }}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors bg-red-500 hover:bg-red-600 text-white"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={showLogoutConfirm}
                onOpenChange={setShowLogoutConfirm}
                title="Log out"
                description="You will be signed out of the teacher portal and returned to login."
                confirmText="Log out"
                cancelText="Stay logged in"
                onConfirm={handleLogoutConfirmed}
                onCancel={() => setShowLogoutConfirm(false)}
            />
        </header>
    );
}
